import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
/**
 * Outil de répartition des sorties — « déshabiller une sortie » (dump
 * utilisateur 2026-09-10). Quand un véhicule tombe en panne ou doit être
 * retiré, le régulateur prend un véhicule sur une **sortie qui finit tôt**
 * pour couvrir les **courses restantes d'une sortie qui finit tard**, en
 * respectant le type de matériel (articulé / standard, aptitude TEOR,
 * restriction de ligne) et le dépôt.
 *
 * Deux modes :
 *  - ciblé (`?target=<runId>`) → `target` renseigné + `matches` = donneurs
 *    classés pour cette sortie ;
 *  - balayage (sans `target`) → `targets` = sorties à découvert, `donors` = le
 *    pool de véhicules bientôt libres, `matches` = les meilleures reprises.
 *
 * **Sentinelle conseille, n'agit pas** : `GET` seul, aucune affectation
 * appliquée, aucun verbe mutant.
 */
export const VehicleClass = z.enum(["standard", "articulated", "metro"]);
export const SortieVehiclePhase = z.enum([
    "ending-soon", // encore en course, mais la dernière de la sortie se termine bientôt
    "hlp", // entre deux courses de sa sortie (haut-le-pied)
    "returning", // dernière course finie, HLP retour vers le dépôt
    "at-depot", // rentré (après l'heure de Rentrée)
]);
/** Un véhicule bientôt libre — donneur potentiel. */
export const SortieReallocationVehicle = z.object({
    runId: z.string(),
    lineId: OpaqueId,
    lineCode: z.string(),
    sortie: z.string(),
    journeyId: OpaqueId,
    vehicle: z
        .object({ displayNumber: z.string(), operatorId: z.string() })
        .nullable(),
    vehicleType: z.object({
        class: VehicleClass,
        teor: z.enum(["none", "capable", "only"]),
        propulsion: z.enum(["diesel", "electric", "hydrogen"]),
        /** `true` si le n° de parc est tombé dans une plage `config.vehicleTypes`. */
        matched: z.boolean(),
    }),
    position: z.object({ lat: z.number(), lon: z.number() }).nullable(),
    phase: SortieVehiclePhase,
    /** Instant où le véhicule peut prendre une nouvelle affectation. */
    freeAt: IsoDateTime,
    /** Dernier arrêt de sa dernière course (ou le dépôt s'il est déjà rentré). */
    freeAtStopName: z.string().nullable(),
    rentreeAt: IsoDateTime.nullable(),
    depot: z.object({ id: z.string(), label: z.string() }).nullable(),
});
/** Une sortie qui a besoin d'un véhicule. */
export const SortieReallocationTarget = z.object({
    runId: z.string(),
    lineId: OpaqueId,
    lineCode: z.string(),
    sortie: z.string(),
    /** `true` si une course la concerne maintenant ET une course live la rapproche. */
    covered: z.boolean(),
    /** Index (0-based) de la 1re course encore à assurer. */
    nextCourseIndex: z.number().int().nonnegative(),
    nextCourseDepartsAt: IsoDateTime,
    nextCourseFromStopName: z.string(),
    remainingCoursesCount: z.number().int().nonnegative(),
    remainingServiceSeconds: z.number().int().nonnegative(),
    rentreeAt: IsoDateTime.nullable(),
    depot: z.object({ id: z.string(), label: z.string() }).nullable(),
    /**
     * Classe du véhicule effectivement sur la sortie quand elle est couverte.
     * Quand elle ne l'est pas, la classe admissible s'il n'y en a qu'une, sinon
     * un repli — lire `admissibleClasses`, qui est la donnée sûre.
     */
    requiredClass: VehicleClass,
    /**
     * v1.51 — classes de matériel **admissibles** sur la ligne, dérivées de la
     * table de parc de l'exploitant (`config.vehicleTypes` : restrictions de
     * ligne, aptitude TEOR, voie ferrée pour le métro). Sur une sortie à
     * découvert, c'est la seule information fiable : il n'y a aucun véhicule dont
     * lire la classe. Une ligne TEOR n'admet que des articulés, le métro que des
     * rames. Ensemble vide = aucune série déclarée ne convient (table incomplète).
     */
    admissibleClasses: z.array(VehicleClass),
    isTeorLine: z.boolean(),
});
/** Une reprise possible : un donneur couvre les courses restantes d'une cible. */
export const SortieReallocationMatch = z.object({
    donorRunId: z.string(),
    targetRunId: z.string(),
    hlpSeconds: z.number().int().nonnegative().nullable(),
    hlpSource: z.enum(["osrm", "none"]),
    readyAt: IsoDateTime,
    /** Index de la 1re course de la cible que le donneur peut réellement prendre. */
    catchesCourseIndex: z.number().int().nonnegative().nullable(),
    catchesCourseDepartsAt: IsoDateTime.nullable(),
    coursesCoveredCount: z.number().int().nonnegative(),
    /** Courses parties sans personne avant que le donneur soit prêt. */
    coursesMissedCount: z.number().int().nonnegative(),
    classCompatible: z.boolean(),
    aptitudeOk: z.boolean(),
    sameDepot: z.boolean(),
    /**
     * v1.51 — **ce que coûterait de prendre ce véhicule tout de suite**, sans
     * attendre la fin de sa sortie : nombre de courses que la sortie donneuse
     * abandonnerait, et durée de service perdue. C'est l'autre moitié de
     * l'arbitrage « quelle sortie sacrifier » — `coursesCoveredCount` dit ce
     * qu'on gagne, ceci dit ce qu'on perd. `0` = le donneur a déjà fini, il n'y a
     * rien à sacrifier.
     *
     * Sentinelle **ne déclenche rien** : ces chiffres éclairent la décision du
     * régulateur, ils ne décrivent aucune action engagée.
     */
    donorSacrificedCoursesCount: z.number().int().nonnegative(),
    donorSacrificedServiceSeconds: z.number().int().nonnegative(),
    outcome: z.enum(["full", "partial", "none"]),
    feasibility: z.enum(["feasible", "uncertain"]),
});
export const SortieReallocationResponse = z.object({
    at: IsoDateTime,
    seq: z.number().int(),
    /** Renseigné en mode `?target=` (la sortie nommée). */
    target: SortieReallocationTarget.nullable(),
    /** Balayage : sorties à découvert (Rentrée la plus lointaine d'abord). */
    targets: z.array(SortieReallocationTarget),
    /** Pool de véhicules bientôt libres. */
    donors: z.array(SortieReallocationVehicle),
    /** Reprises classées (`full` > `partial` > `none`, puis + de courses couvertes, puis HLP court). */
    matches: z.array(SortieReallocationMatch),
});
//# sourceMappingURL=sortie-reallocation.js.map